import React, { useCallback, useContext, useMemo } from 'react';
import styled, { css } from 'styled-components';
import moment from 'moment';
import { required, onlyPositiveNumber } from '@ubeya/shared/utils/form';
import { FlexSpace, FormModal as BaseFormModal, InputField, DateField } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { responsiveFields } from '@ubeya/shared-web/style';
import useAccountConfig from '@ubeya/shared/hooks/account/useAccountConfig';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import { isMobileOnly } from 'react-device-detect';
import { DEFAULT_POSITION_RATE_EFFECTIVE_DATE } from 'settings/constants';

const Container = styled(FlexSpace)`
  ${responsiveFields.mobile};
`;

const FormModal = styled(BaseFormModal)`
  ${({ $isMobileOnly }) =>
    $isMobileOnly &&
    css`
      max-height: 65vh;
    `}
`;

const PositionWageModal = ({
  showBillRate,
  item,
  onClose,
  onConfirm,
  validate,
  showDefaultPositionEffectiveDate = true,
  disableAllWagesActions
}) => {
  const { t } = useContext(ConfigContext);
  const { currency } = useContext(ConfigContext);
  const { account } = useAccountConfig();

  const isFlatWage = account?.payrollConfig?.flatIsActive;

  const { wage, clientWage } = item || {};

  const initialValues = useMemo(
    () => ({
      ...item,
      effectiveDate:
        item.effectiveDate || (showDefaultPositionEffectiveDate ? DEFAULT_POSITION_RATE_EFFECTIVE_DATE : null),
      wage: {
        ...wage,
        flatHours: !wage || wage.flatHours === null || wage.flatHours === 0 ? '' : wage.flatHours,
        hourlyWage: !wage || wage.hourlyWage === null || wage.hourlyWage === 0 ? '' : wage.hourlyWage,
        flatWage: !wage || wage.flatWage === null || wage.flatWage === 0 ? '' : wage.flatWage
      },
      clientWage: {
        ...clientWage,
        flatHours:
          !clientWage || clientWage.flatHours === null || clientWage.flatHours === 0 ? '' : clientWage.flatHours,
        hourlyWage:
          !clientWage || clientWage.hourlyWage === null || clientWage.hourlyWage === 0 ? '' : clientWage.hourlyWage,
        flatWage: !clientWage || clientWage.flatWage === null || clientWage.flatWage === 0 ? '' : clientWage.flatWage
      }
    }),
    [clientWage, item, showDefaultPositionEffectiveDate, wage]
  );

  const handleSubmit = useCallback(
    (values) =>
      onConfirm({
        ...values,
        effectiveDate: moment(values.effectiveDate).format(API_DATE_FORMAT)
      }),
    [onConfirm]
  );

  return (
    <FormModal
      initialValues={initialValues}
      onSubmit={handleSubmit}
      size="small"
      onClose={onClose}
      title={`${item?.id ? t('editWage') : t('addWage')}`}
      confirmText={t('save')}
      showCancelButton
      validate={validate}
      $isMobileOnly={isMobileOnly}
      disabledOnPristine={false}>
      <Container>
        <InputField
          name="wage.hourlyWage"
          type="number"
          title={`${t('hourlyWage')} (${currency})`}
          onChange={onlyPositiveNumber}
          min={0}
          autoFocus
          required
          validate={required}
          isDisabled={disableAllWagesActions}
        />
        {isFlatWage && (
          <>
            <InputField
              name="wage.flatWage"
              title={`${t('flatWage')} (${currency})`}
              onChange={onlyPositiveNumber}
              type="number"
              isDisabled={disableAllWagesActions}
            />
            <InputField
              name="wage.flatHours"
              title={t('flatHours')}
              onChange={onlyPositiveNumber}
              type="number"
              isDisabled={disableAllWagesActions}
            />
          </>
        )}

        {showBillRate && (
          <>
            <InputField
              name="clientWage.hourlyWage"
              type="number"
              title={`${t('positionModalBillRate')} (${currency})`}
              onChange={onlyPositiveNumber}
              min={0}
              isDisabled={disableAllWagesActions}
            />
            {isFlatWage && (
              <>
                <InputField
                  name="clientWage.flatWage"
                  title={`${t('globalWage')} (${currency})`}
                  onChange={onlyPositiveNumber}
                  type="number"
                  isDisabled={disableAllWagesActions}
                />
                <InputField
                  name="clientWage.flatHours"
                  title={t('flatBillRateHoursLimit')}
                  onChange={onlyPositiveNumber}
                  type="number"
                  isDisabled={disableAllWagesActions}
                />
              </>
            )}
          </>
        )}
        <DateField
          name="effectiveDate"
          title={t('effectiveFrom')}
          type="date"
          validate={required}
          required
          usePortal
          clearable={!disableAllWagesActions}
          isDisabled={disableAllWagesActions}
        />
      </Container>
    </FormModal>
  );
};

export default PositionWageModal;
